import { useEffect, useState } from 'react'
import { useQueryParams, useLocation } from '@fs/zion-router'

import { formatWithRules } from 'helpers/dateUtils'
import transl from 'views/SearchResultsPage/SearchResultsPage.transl'
import { useLocalDataCache } from './useLocalDataCache'

// These are params that are not used when setting the page title
const paramsToIgnore = ['advanced', 'jiapuOnly', 'page', 'volumeSetSearch']
const queryParamTitleText = {
  needsToBeOrganized: transl.needsToBeOrganizedSearch,
}
const queryParamPriority = [
  'needsToBeOrganized',
  'startDateAvailable',
  'surname',
  'ancestralHall',
  'firstAncestor',
  // NOTE: honoredAncestor is deprecated but left here for backwards compatibility
  'honoredAncestor',
  'generationPoem',
  'imageGroupNumbers',
  'creator',
  'custodian',
  'cameraOperatorId',
  'keyword',
  'projectId',
  'naturalGroupIds',
  'apid',
  'ark',
  'titleNumber',
]

export default function useSearchResultsPageTitle({ place }) {
  const { lifeEvents } = useLocalDataCache()
  const { query } = useQueryParams()
  const [pageTitle, setPageTitle] = useState(transl.loading())
  const [date, setDate] = useState(null)
  const { pathname } = useLocation()

  // format the date if there is a new search
  const [dateQueries, setDateQueries] = useState(null)
  const startDate = query?.startDate
  const endDate = query?.endDate
  useEffect(() => {
    if (startDate || endDate) {
      const isNewSearch = startDate !== dateQueries?.startDate || endDate !== dateQueries?.endDate
      if (isNewSearch) {
        setDateQueries({ startDate, endDate })
        const locale = navigator.languages[0]
        const formattedDate = formatWithRules({ fromISOString: startDate, toISOString: endDate, locale })
        setDate(formattedDate)
      }
    } else if (date !== null) setDate(null)
  }, [date, dateQueries, startDate, endDate])

  useEffect(() => {
    if (!pathname.includes('search-results')) return

    if (Object.keys(query).every((param) => paramsToIgnore.includes(param)) && query.volumeSetSearch) {
      setPageTitle(transl.allPlaces())
      return
    }

    let title

    queryParamPriority.some((queryParam) => {
      if (query[queryParam]) {
        title = queryParamTitleText[queryParam] ? queryParamTitleText[queryParam]() : query[queryParam]
        return true
      }

      return false
    })

    // If the title hasn't been set yet, set the title based on place, date, and life event query params
    if (!title) {
      const titleElements = []
      if (query.place && place?.id) {
        titleElements.push(place.name)
      }
      if (date) {
        titleElements.push(date)
      }
      if (query.lifeEvent) {
        titleElements.push(lifeEvents.find((lifeEvent) => lifeEvent?.id === query.lifeEvent)?.name)
      }
      title = titleElements.join(' | ')
    }

    setPageTitle(title || transl.exploreImages())
  }, [date, lifeEvents, pathname, place, query])

  return pageTitle
}
